"use client";

import ChatIcon from "@mui/icons-material/Chat";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Menu, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { CONTACT_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import useRemoveContact from "@/hooks/requests/contact/use-remove-contact";
import useStartConversation from "@/hooks/requests/conversations/use-start-conversation";
import { useNotification } from "@/contexts/notification-context";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import IContact from "@/interfaces/contact.interface";
import { ContactsTableMessages } from "@/messages/contacts/contacts-table.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

const WHATSAPP_WINDOW_MS = 24 * 60 * 60 * 1000;

// Fora da janela de 24h o WhatsApp só aceita template, então o STARTCONVERSATION precisa ficar liberado.
const isWhatsappWindowOpen = (lastReceivedDate?: string | null) => {
  if (!lastReceivedDate) return false;
  const receivedAt = new Date(lastReceivedDate).getTime();
  return !Number.isNaN(receivedAt) && Date.now() - receivedAt < WHATSAPP_WINDOW_MS;
};

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: ContactsTableMessages;
  selected: IContact;
}

export function ContactPopover({ isOpen, anchorEl, onClose, messages, selected }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { mutate } = useMutate();
  const { remove, isLoading } = useRemoveContact();
  const { startConversation: startWhatsAppConversation, isLoading: isLoadingWhatsAppConversation } =
    useStartConversation();
  const { chats, activeChatPlatformId } = useNotification();
  const { showToast } = useToast();
  const hasActiveConversation = !!chats?.some(
    (c) =>
      c.contactId === selected.id &&
      !c.endDate &&
      isWhatsappWindowOpen(c.lastReceivedDate)
  );
  const isInactive = selected.active === false;

  const { isVisible, handleOpen, handleClose } = useDialog<IContact>();

  const hasNoPermissionToDelete = useHasNoPermission(CONTACT_PERMISSIONS.DELETE);
  const hasNoPermissionToEdit = useHasNoPermission(CONTACT_PERMISSIONS.EDIT);
  const hasNoPermissionToCreateConversation = useHasNoPermission(CONTACT_PERMISSIONS.CREATE);

  const onSuccess = () => {
    mutate(SWR_KEYS.getContacts);
    handleClose();
  };

  const onError = () => handleClose();

  const onConfirmRemove = async (contact: IContact) => {
    const successMessage = messages.deleteContactSuccess;
    const errorMessage = messages.deleteContactError;

    const _id = contact.id;
    await remove({ _id, onSuccess, onError, successMessage, errorMessage });
  };

  const redirect = () => router(ROUTE_URLS.updateContact(lang, selected.id));

  const startConversation = async () => {
    try {
      if (isInactive) {
        showToast({ text: messages.startConversationInactive, type: "error" });
        onClose();
        return;
      }

      if (!activeChatPlatformId) {
        onClose();
        return;
      }

      await startWhatsAppConversation({
        formData: {
          contactId: selected.id,
          platformId: activeChatPlatformId,
        },
        successMessage: messages.startConversationSuccess,
        errorMessage: messages.startConversationError,
        onSuccess: () => {
          router(ROUTE_URLS.chat(lang));
          onClose();
        },
        onError: () => {
          onClose();
        },
      });
    } catch (error) {
      onClose();
    }
  };

  const startConversationItem = (
    <NotificationItem
      icon={<ChatIcon />}
      disabled={
        hasNoPermissionToCreateConversation ||
        !activeChatPlatformId ||
        isLoadingWhatsAppConversation ||
        hasActiveConversation ||
        isInactive
      }
      onClick={startConversation}
      title={messages.startConversation}
    />
  );

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      {isInactive ? (
        <Tooltip title={messages.startConversationInactive}>
          <span>{startConversationItem}</span>
        </Tooltip>
      ) : (
        startConversationItem
      )}
      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={redirect}
        title={messages.updatePopover}
      />

      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpen(selected)}
        title={messages.removePopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => onConfirmRemove(selected)}
        titleText={messages.deleteContactDialogTitle}
        descriptionText={messages.deleteContactDialogContent}
        cancelText={messages.deleteContactDialogCancel}
        confirmText={messages.deleteContactDialogConfirm}
        isLoading={isLoading}
      />
    </Menu>
  );
}
